import React from 'react';
import { CustomStyleTable } from '../Styles';
import { Box } from '@mui/system';
import NotInformation from '../NotInformation';
import CustomTh from './CustomTh';
import CustomTr from './CustomTr';
import CustomTd from './CustomTd';
import { CustomCheckbox } from '../CustomCheckbox';
import CustomTableApp from "./Functionality/CustomTableApp";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @Description Table with dynamic content
 * @param String tableId: Identifier for the table (required)
 * @param String searchId: Identifier for the search field (optional)
 * @param Boolean checkbox: Show checkbox in the first column (optional)
 * @param String checkboxTitle: Show title in the tooltip for the checkbox in <thead> (optional)
 * @param Boolean isAllChecked: Show checkbox as checked in <thead> (optional)
 * @param Array<JSX.Element> thead: Dynamic list of <th> for <thead> (required)
 * @param Array<JSX.Element> tbody: Dynamic list of <JSX.Element> for <tbody> (required)
 * @param Boolean columnActions: Show column for action buttons (optional)
 * @param UseState selectedList: List of selected elements to show as checked=true in the checkbox column (optional)
 * @param Function getActionsButtons: Function to get buttons (JSX.Element) and show them in the action column (optional)
 * @param Event onChangeCheckbox: Checked/unchecked trigger for the <thead> checkbox column (optional)
 * @param Event handleCheckboxChange: Checked/unchecked trigger for the <tbody> checkbox column (optional)
 * @param {string|null} [textNotInformation=null] - Custom text when no information is available (optional).
 * @param {boolean} showHeader Boolean: Controls whether the table header is displayed.
 * @param JSX.Element render: Optional extra content to render below the table
 * @returns {JSX.Element}
 */

const CustomTable = ({
     tableId,
     searchId,
     checkbox = false,
     checkboxTitle = 'de_/_select_all',
     isAllChecked,
     thead,
     tbody,
     columnActions = true,
     selectedList = [],
     getActionsButtons = (item, index) => { },
     onChangeCheckbox,
     handleCheckboxChange = (e) => { },
     inModal = false,
     withoutHeight = false,
     listDisabled = [],
     textNotInformation = null,
     childrenNotInformation,
     render = null,
     checkboxFixed=true,
     showHeader = true
 }) => {
    const {
        t,
        elementRef,
        tbodyRef,
        finalText,
        isSearchEmpty
    } = CustomTableApp({
        tableId,
        searchId,
        checkbox,
        thead,
        tbody,
        inModal,
        textNotInformation ,
        childrenNotInformation,
        withoutHeight
    });

    return (
        <>
            {tbody?.body?.length > 0 ? (
                <div className={'custom-panel-table'}>
                    <Box sx={CustomStyleTable} ref={elementRef}>
                        <table id={tableId} className={`${withoutHeight ? "" : "custom-scroll-table"} w-100 custom-scrollbar`}>
                            <thead className={`${!showHeader ? 'collapse-visibility': ''}`}>
                            {checkbox && (
                                <CustomTh tableId={tableId} fixedXY={checkboxFixed} left={0} classes='checkbox-table'>
                                    <CustomCheckbox
                                        id={'cb_ctb_de_select_all'}
                                        tooltip={t(checkboxTitle)?.toUpperCase()}
                                        name={'deSelectAll'}
                                        checked={isAllChecked}
                                        indeterminate={
                                            selectedList.length > 0 && selectedList.length < tbody?.data?.length
                                        }
                                        onChange={onChangeCheckbox}
                                    />
                                </CustomTh>
                            )}
                            {thead}
                            {columnActions && <CustomTh classes={"justify-center-th"} tableId={tableId}>{t('options')}</CustomTh>}
                            </thead>

                            <tbody ref={tbodyRef}>
                            {tbody?.body?.map((e, index) => {
                                return (
                                    <CustomTr key={tbody?.data[index]?.id} classes={tbody?.data[index]?.trClass}>
                                        {checkbox && (
                                            <CustomTd
                                                tableId={tableId}
                                                fixed={checkboxFixed}
                                                left={0}
                                                classes={`checkbox-table ${checkboxFixed ? 'checkbox-fixed' : ''} ${tbody?.data[index]?.tdCheckboxClass}`}
                                            >
                                                <CustomCheckbox
                                                    id={'cb_ctb_row_' + index}
                                                    checked={selectedList?.includes(tbody?.data[index]?.id)}
                                                    onChange={(e) => {
                                                        handleCheckboxChange({
                                                            target: {
                                                                id: tbody?.data[index]?.id,
                                                                checked: e.target.checked,
                                                            },
                                                        });
                                                    }}
                                                    disabled={listDisabled.some(disabledItem => {
                                                        const currentItem = tbody?.data[index];
                                                        return Object.entries(disabledItem).every(([key, value]) => currentItem?.[key] === value);
                                                    })}
                                                />
                                            </CustomTd>
                                        )}
                                        {e}

                                        {columnActions && (
                                            <CustomTd tableId={tableId} classes={`${tbody?.data[index]?.tdActionsClass}`}>
                                                <div className={"tableIcon"}>
                                                    {getActionsButtons(tbody?.data[index], index)}
                                                </div>
                                            </CustomTd>
                                        )}
                                    </CustomTr>
                                );
                            })}
                            </tbody>
                        </table>
                        {render && render}
                    </Box>
                </div>
            ) : ( <NotInformation
                    text={finalText}
                    card={true}
                    searchElement={isSearchEmpty} //activates 'no_results_found' when applied
                >
                    {childrenNotInformation}
                </NotInformation>
            )}
        </>
    );
};

export default CustomTable;
